import { PublicIdSchemas } from '@idc-forms/id'
import { z } from 'zod'
import { STATE_TYPE } from '../entities/state/state'

/**
 * Contrato de `GET /api/v2/me/state-transitions`: los estados del flujo que el
 * usuario autenticado puede LEER. Reemplaza al verb path legacy
 * `GET /states/my-transitions`.
 *
 * Alimenta los filtros del tablero de negociaciones, y por eso lo guardan
 * permisos de NEGOCIACIONES y no del módulo de estados: un asesor que trabaja
 * expedientes necesita su tablero aunque no tenga `ReadStates`.
 *
 * QUIÉN VE QUÉ, que es todo el contenido de negocio de este endpoint:
 *
 * - Admin o `ReadAllNegotiations` → el catálogo entero.
 * - Cualquier otro → sólo los estados donde su rol le concede
 *   `ReadStateTransition`. Tener `Edit` o `Execute` sobre un estado NO alcanza:
 *   son permisos de escritura y la pregunta acá es de lectura.
 * - Sin ningún `statePermission` legible → lista vacía. Falla CERRADO: en un
 *   endpoint que reparte visibilidad, el default seguro es no mostrar nada.
 *
 * DELIBERADAMENTE NO SE FILTRA POR `active`, a diferencia de
 * `GET /admin/states/lookup`. Son dos preguntas distintas y conviene que quede
 * escrito por qué divergen:
 *
 * | | `admin/states/lookup` | este endpoint |
 * | --- | --- | --- |
 * | Pregunta | ¿a qué estado puedo ASIGNAR algo? | ¿qué estados puedo VER? |
 * | Uso | selector de escritura | filtro de lectura sobre datos históricos |
 * | Estado inactivo | se oculta — ofrecerlo sería ofrecer una asignación inválida | **se incluye** |
 *
 * Un estado desactivado puede conservar negociaciones vivas —hoy en `dev` el
 * estado con más carga tiene 152—, y un filtro que no lo ofrece las vuelve
 * inalcanzables desde el tablero SIN dar error. Es el modo de falla caro: la
 * pantalla se ve sana y muestra de menos. Además es la paridad exacta con v1,
 * que consulta `find({})` sin filtro, así que incluirlos no es una decisión
 * nueva: es no introducir una regresión.
 */
export const V2MyStateTransitionItemSchema = z.object({
  id: PublicIdSchemas.state,
  name: z.string().min(1),
  color: z.string(),
  type: z.nativeEnum(STATE_TYPE)
})

/**
 * Sin paginar, igual que el lookup: el catálogo de estados es chico y acotado
 * del lado del servidor. La forma `{ data }` es la del envelope de listado v2.
 */
export const V2MyStateTransitionsSchema = z.object({
  data: z.array(V2MyStateTransitionItemSchema)
})

export type V2MyStateTransitionItem = z.infer<typeof V2MyStateTransitionItemSchema>
export type V2MyStateTransitions = z.infer<typeof V2MyStateTransitionsSchema>
